import { useEffect, useRef, useState } from 'react';
import LanguageRewriteButton from '../../../components/LanguageRewriteButton';
import CommentPreviewButton from '../../../components/CommentPreviewButton';
import DirectorNotes from './DirectorNotes';
import { Resource, ResourceReviewItem, Shot } from '../../../types';
import { listShotComments } from '../../../api';
import { creditsToYuan } from '../../../utils/currency';
import { getBlockingResourceReviewsForShot, hasBlockingPresetAudio } from '../utils/resourceReviewGate';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface ShotActionBarProps {
  shot: Shot;
  languageSkillId?: string;
  languageSkillName: string;
  applyingLanguage: boolean;
  rematching: boolean;
  generating: boolean;
  resourceReviews: ResourceReviewItem[];
  resources: Resource[];
  credits: number | null;
  pendingCommentCount: number;
  latestPendingComment?: string;
  t: Translate;
  onDelete: () => void;
  onOpenComments: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onRematchAssets: () => void;
  onApplyLanguage: () => void;
  onGenerateVideo: () => void;
  editing?: boolean;
  savingShot?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  onSaveAndGenerate?: () => void;
}

export default function ShotActionBar({
  shot,
  languageSkillId,
  languageSkillName,
  applyingLanguage,
  rematching,
  generating,
  resourceReviews,
  resources,
  pendingCommentCount,
  latestPendingComment,
  credits,
  t,
  onDelete,
  onOpenComments,
  onEdit,
  onDuplicate,
  onRematchAssets,
  onApplyLanguage,
  onGenerateVideo,
  editing = false,
  savingShot = false,
  onSave,
  onCancel,
  onSaveAndGenerate,
}: ShotActionBarProps) {
  const blockingResourceReviews = getBlockingResourceReviewsForShot(shot.id, resources, resourceReviews);
  const presetAudioBlocking = hasBlockingPresetAudio(blockingResourceReviews);
  const resourceReviewing = blockingResourceReviews.processing.length > 0
    || blockingResourceReviews.unsubmitted.length > 0
    || presetAudioBlocking;
  const hasPending = pendingCommentCount > 0;

  // 「更多」菜单用 fixed 定位渲染，避免被外层卡片的 overflow: clip 裁剪。
  const [moreOpen, setMoreOpen] = useState(false);
  const [morePosition, setMorePosition] = useState<{ top: number; left: number } | null>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  const toggleMore = () => {
    if (moreOpen) {
      setMoreOpen(false);
      return;
    }
    const rect = moreButtonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMorePosition({ top: rect.bottom + 4, left: rect.left });
    setMoreOpen(true);
  };

  const runMoreAction = (action: () => void) => {
    setMoreOpen(false);
    action();
  };

  const [fetchedComment, setFetchedComment] = useState('');
  useEffect(() => {
    if (!hasPending) { setFetchedComment(''); return; }
    let cancelled = false;
    listShotComments(shot.id).then(comments => {
      if (cancelled) return;
      const pending = comments.filter(c => !c.isResolved);
      const latest = pending[pending.length - 1];
      setFetchedComment(latest?.content || '');
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [shot.id, hasPending, pendingCommentCount]);

  if (editing) {
    const saveAndGenerateLabel = credits != null
      ? t('shot.saveAndGenerateWithCost', { cost: creditsToYuan(credits) })
      : t('shot.saveAndGenerate');
    return (
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary" style={{ fontSize: '0.85rem' }} onClick={onSave} disabled={savingShot}>
          {savingShot ? <><span className="loading-spinner"></span>{t('shot.savingShot')}</> : t('save', { ns: 'common' })}
        </button>
        <button className="btn btn-secondary" style={{ fontSize: '0.85rem' }} onClick={onCancel} disabled={savingShot}>
          {t('cancel', { ns: 'common' })}
        </button>
        <button
          className="btn"
          style={{ fontSize: '0.85rem', background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', color: '#fff', border: 'none' }}
          onClick={onSaveAndGenerate}
          disabled={savingShot || generating || resourceReviewing}
        >
          {saveAndGenerateLabel}
        </button>
      </div>
    );
  }

  const commentText = latestPendingComment || fetchedComment;

  return (
    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
      <CommentPreviewButton
        count={pendingCommentCount}
        latestComment={commentText}
        viewNewLabel={t('comments.viewNew')}
        buttonLabel={t('comments.button')}
        onClick={onOpenComments}
        size="md"
      />
      <DirectorNotes
        notes={shot.directorNoteItems?.length
          ? shot.directorNoteItems
          : (shot.directorNotes || []).map(text => ({ text }))}
      />
      <div style={{ flex: 1 }} />
      <button
        ref={moreButtonRef}
        className="btn btn-secondary"
        style={{ fontSize: '0.85rem' }}
        aria-haspopup="menu"
        aria-expanded={moreOpen}
        onClick={toggleMore}
      >
        {t('shot.more')} ▾
      </button>
      {moreOpen && morePosition && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }} onClick={() => setMoreOpen(false)} />
          <div
            role="menu"
            style={{
              position: 'fixed', top: morePosition.top, left: morePosition.left, zIndex: 1001,
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: '0.25rem',
              display: 'flex', flexDirection: 'column', minWidth: '120px',
            }}
          >
            <button
              role="menuitem"
              type="button"
              style={{
                background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer',
                padding: '0.45rem 0.75rem', fontSize: '0.85rem', borderRadius: '6px', color: 'var(--text)',
              }}
              onClick={() => runMoreAction(onDuplicate)}
            >
              {t('shot.duplicate')}
            </button>
            <button
              role="menuitem"
              type="button"
              style={{
                background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer',
                padding: '0.45rem 0.75rem', fontSize: '0.85rem', borderRadius: '6px', color: 'var(--danger)',
              }}
              onClick={() => runMoreAction(onDelete)}
            >
              {t('delete', { ns: 'common' })}
            </button>
          </div>
        </>
      )}
      <button className="btn btn-secondary" style={{ fontSize: '0.85rem' }} onClick={onEdit}>{t('edit', { ns: 'common' })}</button>
      <button
        className="btn btn-secondary"
        style={{ fontSize: '0.85rem' }}
        onClick={onRematchAssets}
        disabled={rematching}
      >
        {rematching ? <><span className="loading-spinner"></span>{t('shot.matchingAssets')}</> : t('shot.matchAssets')}
      </button>
      {languageSkillId && (
        <LanguageRewriteButton
          shot={shot}
          onApply={onApplyLanguage}
          disabled={applyingLanguage}
          skillName={languageSkillName}
        />
      )}
      <button
        className="btn"
        style={{ fontSize: '0.85rem', background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', color: '#fff', border: 'none' }}
        onClick={onGenerateVideo}
        disabled={generating || resourceReviewing}
        title={!generating && presetAudioBlocking ? t('shot.presetAudioReviewingHint') : undefined}
      >
        {generating
          ? <><span className="loading-spinner"></span>{t('shot.submittingTask')}</>
          : presetAudioBlocking
            ? t('shot.presetAudioReviewing')
            : resourceReviewing
              ? t('shot.resourceReviewing')
              : credits != null
                ? t('shot.generateVideoWithCost', { cost: creditsToYuan(credits) })
                : t('shot.generateVideo')}
      </button>
    </div>
  );
}
